import React, { useState } from "react";
import Comment from "./Comment";
import {
    Avatar,
    Box,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { CommentInputWithPersonTagging } from "./Comments";
import TextsmsOutlinedIcon from "@mui/icons-material/TextsmsOutlined";
import { useSelector } from "react-redux";
import { selectName, selectProfileImage } from "@/auth/authSlice";

const CommentThread = ({
    rootComment,
    onResolve,
    onReply,
    onDelete,
    onCancel,
    onEdit,
    onThreadAction,
    onInputFocus,
    onInputBlur,
    showCommentButton,
}) => {
    const [hovering, setHovering] = useState(false);
    const comments = (rootComment ? [rootComment] : []).concat(
        rootComment?.children || [],
    );
    const handleEdit = (commentId, commentInput) => {
        onEdit(commentId, commentInput);
    };
    const handleReply = (commentInput) => {
        onReply(rootComment?.id, commentInput);
    };
    const handleClick = (e) => {
        onThreadAction(rootComment);
    };
    const handleFocus = (e) => {
        if (onInputFocus) {
            onInputFocus(rootComment);
        }
    };
    const handleBlur = (e) => {
        if (onInputBlur) {
            onInputBlur(rootComment);
        }
    };
    const reduxPersonName = useSelector(selectName);
    const image = useSelector(selectProfileImage);
    return (
        <Paper
            onMouseEnter={() => setHovering(onThreadAction && true)}
            onMouseLeave={() => setHovering(false)}
            sx={{
                p: 0,
                mb: 2,
                borderRadius: 3,
                boxShadow: "0 1px 4px 0 rgba(0,0,0,0.18)",
                overflow: "hidden",
                padding: 2,
                paddingTop: 3,
                opacity: hovering ? 0.85 : 1,
                minWidth: 280,
            }}
        >
            {comments.map((comment) => (
                <Box paddingBottom={2} key={comment.id} onClick={handleClick}>
                    <Comment
                        key={comment.id}
                        personName={comment.person_name}
                        personId={comment.person_id}
                        iconUrl={comment.profile_image_icon_url}
                        timePosted={comment.created_at}
                        body={comment.description}
                        resolved={comment.archived_at !== null}
                        onResolve={() => onResolve(comment.id)}
                        onEdit={(editContent) =>
                            handleEdit(comment.id, editContent)
                        }
                        onDelete={() => onDelete(comment.id)}
                    />
                </Box>
            ))}

            {!rootComment && (
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ paddingBottom: 2, paddingTop: 1 }}
                >
                    <Avatar
                        src={image}
                        alt={reduxPersonName}
                        sx={{ width: 22, height: 22, mr: 1.5 }}
                    />
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            {reduxPersonName}
                        </Typography>
                    </Box>
                </Box>
            )}
            <CommentInput
                onCancel={onCancel}
                onCommentInputConfirm={handleReply}
                onFocus={handleFocus}
                onBlur={handleBlur}
                showCommentButton={showCommentButton}
                confirmButtonName={rootComment ? "Reply" : "Comment"}
            />
        </Paper>
    );
};

export const CommentInput = ({
    confirmButtonName = "Comment",
    onCommentInputConfirm,
    onCancel,
    onFocus,
    onBlur,
    commentInputInitialValue = "",
    showCommentButton = true,
}) => {
    const [commentInput, setCommentInput] = useState(commentInputInitialValue);
    const theme = useTheme();
    const handleInputConfirm = (e) => {
        e.stopPropagation();
        onCommentInputConfirm(commentInput);
        setCommentInput("");
    };
    const handleCancel = (e) => {
        e.stopPropagation();
        setCommentInput("");
        if (onCancel) {
            onCancel();
        }
    };
    const handleFocus = (e) => {
        e.stopPropagation();
        if (onFocus) {
            onFocus(e);
        }
    };
    const handleBlur = (e) => {
        e.stopPropagation();
        if (onBlur) {
            onBlur(e);
        }
    };
    return (
        <>
            <Box
                sx={{
                    borderRadius: 5,
                    background:
                        theme.palette.mode == "dark" ? "#676767" : "#d7d7d7",
                }}
            >
                <CommentInputWithPersonTagging
                    commentData={commentInput}
                    onCommentInput={(value) => setCommentInput(value)}
                    numRows={1}
                    borderless
                    placeholder="Comment or tag others with @"
                    multiline={true}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
            </Box>
            <Grid container marginTop={1} justifyContent={"right"} spacing={1}>
                {onCancel && (
                    <Grid item>
                        <Button
                            onClick={handleCancel}
                            sx={{ borderRadius: 5 }}
                            variant="contained"
                            color="secondary"
                        >
                            Cancel
                        </Button>
                    </Grid>
                )}
                {showCommentButton && (
                    <Grid item>
                        <Button
                            endIcon={<TextsmsOutlinedIcon />}
                            disabled={commentInput.trim().length == 0}
                            onClick={handleInputConfirm}
                            sx={{ borderRadius: 5 }}
                            variant="contained"
                            color="primary"
                        >
                            {confirmButtonName}
                        </Button>
                    </Grid>
                )}
            </Grid>
        </>
    );
};

export default CommentThread;
